import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Combobox } from '@buildoutinc/blueprint-react/ui/Combobox';
import debounce from 'lodash/debounce';

import { get } from 'helpers/api';

import EmailRecipient from './email_recipient';
import type { Recipient } from './email_recipient';

interface SearchedContact {
  id: number;
  name?: string;
  displayName?: string;
  email?: string;
}

interface Props {
  id?: string;
  recipients: Recipient[];
  onChange: (recipients: Recipient[]) => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_QUERY_LENGTH = 2;

function sameEmail(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

export default function EmailRecipientInput({ id, recipients, onChange }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Recipient[]>([]);
  const requestId = useRef(0);

  const searchContacts = useMemo(() => debounce(async (value: string) => {
    const currentId = ++requestId.current;
    try {
      const contacts: SearchedContact[] = await get('/backoffice/contacts/search', { params: { query: value } });
      if (currentId !== requestId.current) return;
      setResults(
        contacts
          .filter(contact => contact.email)
          .map(contact => ({ name: contact.displayName || contact.name, email: contact.email as string }))
      );
    } catch {
      if (currentId === requestId.current) setResults([]);
    }
  }, 300), []);

  useEffect(() => () => searchContacts.cancel(), [searchContacts]);

  const handleInputChange = (value: string) => {
    setQuery(value);

    if (value.trim().length < MIN_QUERY_LENGTH) {
      searchContacts.cancel();
      setResults([]);
      return;
    }

    void searchContacts(value.trim());
  };

  const options = results.filter(
    result => !recipients.some(recipient => sameEmail(recipient.email, result.email))
  );

  const trimmed = query.trim();
  const newRecipient: Recipient | null =
    EMAIL_PATTERN.test(trimmed) &&
    !recipients.some(recipient => sameEmail(recipient.email, trimmed)) &&
    !options.some(option => sameEmail(option.email, trimmed))
      ? { email: trimmed }
      : null;

  const items = newRecipient ? [newRecipient, ...options] : options;

  return (
    <Combobox
      autoHighlight
      filter={null}
      isItemEqualToValue={(a: Recipient, b: Recipient) => sameEmail(a.email, b.email)}
      items={items}
      itemToStringLabel={(recipient: Recipient) => recipient.name || recipient.email}
      multiple
      value={recipients}
      onInputValueChange={handleInputChange}
      onValueChange={(value: Recipient[]) => {
        const missingLocked = recipients.filter(
          recipient => recipient.locked && !value.some(kept => sameEmail(kept.email, recipient.email))
        );
        onChange([...value, ...missingLocked]);
        setQuery('');
        setResults([]);
      }}
    >
      <Combobox.Chips className="d-flex flex-wrap align-items-center gap-1 border-0 p-0 bg-transparent" id="log-email">
        {recipients.map(recipient => (
          <Combobox.Chip
            aria-label={recipient.email}
            className="email-recipient"
            key={recipient.email}
            showRemove={!recipient.locked}
          >
            <EmailRecipient recipient={recipient} />
          </Combobox.Chip>
        ))}
        <Combobox.Input
          id={id}
          placeholder="Search contacts or type an email address..."
          render={
            <input
              className="border-0 bg-transparent p-0 flex-grow-1"
              style={{ outline: 'none', minWidth: '12rem' }}
            />
          }
        />
      </Combobox.Chips>

      <Combobox.Content>
        <Combobox.Empty>No contacts found</Combobox.Empty>
        <Combobox.List>
          {items.map(item => (
            <Combobox.Item key={item.email} value={item}>
              {item === newRecipient
                ? <span>Add "{item.email}"</span>
                : <EmailRecipient recipient={item} />}
            </Combobox.Item>
          ))}
        </Combobox.List>
      </Combobox.Content>
    </Combobox>
  );
}
